/**
 * The Day Nutrition page's own analysis, on the data meals/filter-data returns for each meal
 * (nutrients by symbol at each size) and the nutrient targets of every diet: how a delivery
 * date's planned day of meals meets the full nutrition targets (the RDAs) and the diet's own
 * targets, and how every day a customer could assemble by swapping meals between diets does.
 */
export const FULL_NUTRITION_DIET = 1;
export const JUDGED_SIZE = 'medium';
export const BREAKFAST = 1;
export const LUNCH = 2;
export const DINNER = 3;
export const MEALS_A_DAY = 3;

const number = value => (value === '' || value === null || value === undefined ? null : Number(value));

/** the delivery date of a meal, as a YYYY-MM-DD string */
export function mealDate(meal) {
  return meal && meal.date ? String(meal.date).slice(0, 10) : null;
}

/**
 * one diet's targets worth judging, each with its nutrient's symbol, name and unit, in name order;
 * targets for the ignored nutrient symbols are left out
 */
export function dietTargets(targetRows, dietId, nutrientsById = {}, ignoredSymbols = []) {
  const ignored = new Set(ignoredSymbols || []);
  return (targetRows || [])
    .filter(row => Number(row.diet) === Number(dietId) && row.nutrient_id)
    .map(row => {
      const nutrient = nutrientsById[row.nutrient_id] || {};
      return {
        nutrient_id: row.nutrient_id,
        symbol: nutrient.nutrientsymbol || row.nutrient_symbol || null,
        name: nutrient.nutrientname || `nutrient ${row.nutrient_id}`,
        unit: nutrient.unit || '',
        target: number(row.target_value),
        lower: number(row.lower_limit),
        upper: number(row.upper_limit),
      };
    })
    .filter(target => target.target || target.lower || target.upper)
    .filter(target => !ignored.has(target.symbol))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** the targets scaled for a day with fewer (or more) than three meals */
export function scaleTargets(targets, mealCount) {
  const factor = mealCount / MEALS_A_DAY;
  const scale = value => (value === null ? null : value * factor);
  return targets.map(target => ({...target, target: scale(target.target), lower: scale(target.lower), upper: scale(target.upper)}));
}

/** true when two sets of targets differ: a nutrient in one but not the other, or any value differing */
export function targetsDiffer(targets, otherTargets) {
  const byId = list => Object.fromEntries(list.map(target => [target.nutrient_id, target]));
  const mine = byId(targets);
  const other = byId(otherTargets);
  const ids = new Set([...Object.keys(mine), ...Object.keys(other)]);
  return [...ids].some(id => {
    const a = mine[id];
    const b = other[id];
    return !a || !b || a.target !== b.target || a.lower !== b.lower || a.upper !== b.upper;
  });
}

/** the nutrients of one target set that differ from another's; what the page shows as the diet's own targets */
export function differingTargets(targets, otherTargets) {
  const other = Object.fromEntries(otherTargets.map(target => [target.nutrient_id, target]));
  return targets.filter(target => {
    const match = other[target.nutrient_id];
    return !match || match.target !== target.target || match.lower !== target.lower || match.upper !== target.upper;
  });
}

/** the day's nutrient totals by symbol over the meals' nutrients (each as meals/filter-data returns the judged size) */
export function sumNutrients(mealNutrientsList) {
  const totals = {};
  for (const nutrients of mealNutrientsList) {
    for (const [symbol, amount] of Object.entries(nutrients || {})) {
      totals[symbol] = (totals[symbol] || 0) + (Number(amount) || 0);
    }
  }
  return totals;
}

/**
 * one day's totals against one set of targets. A target is met when the amount reaches the
 * target value, is not below the lower limit and not above the upper limit; a nutrient no meal
 * carries data for is a failure too ('no data'). score is the percentage of targets met.
 */
export function judgeDay(totals, targets) {
  const results = targets.map(target => {
    const known = !!target.symbol && totals[target.symbol] !== undefined;
    const amount = known ? totals[target.symbol] : null;
    const value = amount || 0;
    const problems = [];
    if (!known) problems.push('no data');
    if (target.target && value < target.target) problems.push('under');
    if (target.lower && value < target.lower) problems.push('below lower limit');
    if (target.upper && value > target.upper) problems.push('over upper limit');
    return {
      ...target,
      amount,
      percent: target.target ? (value / target.target) * 100 : null,
      closeness: known ? closenessOf(value, target) : 0,
      problems,
      met: problems.length === 0,
    };
  });
  const met = results.filter(result => result.met).length;
  return {
    results,
    met,
    total: results.length,
    score: results.length ? (met / results.length) * 100 : null,
    closeness: mean(results.map(result => result.closeness)),
    failures: results.filter(result => !result.met),
  };
}

/**
 * how close an amount gets to a target, 0 to 100: a minimum (target value or lower limit)
 * reached is 100 and half of it 50; a maximum kept to is 100 and one exceeded by a quarter 80
 */
export function closenessOf(value, target) {
  const ratios = [];
  if (target.target) ratios.push(Math.min(1, value / target.target));
  if (target.lower) ratios.push(Math.min(1, value / target.lower));
  if (target.upper) ratios.push(value <= target.upper ? 1 : target.upper / value);
  return ratios.length ? Math.min(...ratios) * 100 : 100;
}

function mean(values) {
  const list = values.filter(value => value !== null && value !== undefined);
  return list.length ? list.reduce((sum, value) => sum + value, 0) / list.length : null;
}

/**
 * a set of meals (one day's picks) against targets, on the nutrients loaded for each meal.
 * Targets scale to the number of meals; a day missing any meal's data is not judged.
 */
export function judgeMeals(meals, nutrientsByMealId, targets) {
  const nutrientsList = meals.map(meal => nutrientsByMealId[meal.id]);
  if (meals.length === 0 || nutrientsList.some(nutrients => !nutrients)) {
    return {meals, judged: false, results: [], failures: [], met: 0, total: targets.length, score: null, closeness: null};
  }
  const scaled = meals.length === MEALS_A_DAY ? targets : scaleTargets(targets, meals.length);
  return {meals, judged: true, complete: meals.length === MEALS_A_DAY, ...judgeDay(sumNutrients(nutrientsList), scaled)};
}

/** the meals grouped by delivery date, then diet, each diet's meals in time-of-day order */
export function groupMeals(meals) {
  const byDate = {};
  for (const meal of meals || []) {
    const date = mealDate(meal);
    if (!date || !meal.diet) continue;
    const byDiet = byDate[date] || (byDate[date] = {});
    (byDiet[meal.diet] || (byDiet[meal.diet] = [])).push(meal);
  }
  for (const byDiet of Object.values(byDate)) {
    for (const list of Object.values(byDiet)) {
      list.sort((a, b) => (a.tod || 0) - (b.tod || 0) || a.id - b.id);
    }
  }
  return byDate;
}

/** the time of day a swap keeps: breakfasts swap with breakfasts, lunches and dinners with either */
export function swapSlot(meal) {
  if (meal.tod === BREAKFAST) return 'breakfast';
  if (meal.tod === LUNCH || meal.tod === DINNER) return 'main';
  return null;
}

function distinctMeals(meals) {
  const seen = new Set();
  return meals.filter(meal => {
    if (seen.has(meal.id)) return false;
    seen.add(meal.id);
    return true;
  });
}

/**
 * every day a customer could assemble from a date's meals by swapping within time of day:
 * one breakfast and two distinct lunch-or-dinner meals from the pool. Every combination is
 * listed when there are at most `limit` of them, otherwise `limit` random ones (sampled).
 */
export function swapCombos(meals, {limit = 20000, random = Math.random} = {}) {
  const pool = distinctMeals(meals || []);
  const breakfasts = pool.filter(meal => swapSlot(meal) === 'breakfast');
  const mains = pool.filter(meal => swapSlot(meal) === 'main');
  const pairs = (mains.length * (mains.length - 1)) / 2;
  const count = breakfasts.length * pairs;
  const pick = list => list[Math.floor(random() * list.length)];

  if (count <= limit) {
    const combos = [];
    for (const breakfast of breakfasts) {
      for (let i = 0; i < mains.length; i++) {
        for (let j = i + 1; j < mains.length; j++) {
          combos.push([breakfast, mains[i], mains[j]]);
        }
      }
    }
    return {combos, count, sampled: false};
  }

  const combos = [];
  while (combos.length < limit) {
    const first = pick(mains);
    let second = pick(mains);
    while (second.id === first.id) second = pick(mains);
    combos.push([pick(breakfasts), first, second]);
  }
  return {combos, count, sampled: true};
}

/** the swap combinations judged, with their meals, in score order (worst first) */
export function judgeCombos(combos, nutrientsByMealId, targets) {
  return combos
    .map(meals => judgeMeals(meals, nutrientsByMealId, targets))
    .filter(judged => judged.judged)
    .sort((a, b) => a.score - b.score || a.meals[0].id - b.meals[0].id);
}

/**
 * what the judged swaps tell: the lower and upper bound, mean and median of the targets met,
 * how many of the combinations fail each target, and the distribution of targets met
 */
export function swapSummary(judgedCombos) {
  const count = judgedCombos.length;
  if (count === 0) {
    return {count: 0, min: null, max: null, mean: null, median: null, failureCounts: [], distribution: []};
  }
  const total = judgedCombos[0].total;
  const metValues = judgedCombos.map(judged => judged.met);
  const sorted = [...metValues].sort((a, b) => a - b);
  const median = count % 2 ? sorted[(count - 1) / 2] : (sorted[count / 2 - 1] + sorted[count / 2]) / 2;

  const failures = {};
  for (const judged of judgedCombos) {
    for (const failure of judged.failures) {
      const entry = failures[failure.nutrient_id] || (failures[failure.nutrient_id] = {
        nutrient_id: failure.nutrient_id,
        symbol: failure.symbol,
        name: failure.name,
        count: 0,
        problems: {},
      });
      entry.count += 1;
      for (const problem of failure.problems) {
        entry.problems[problem] = (entry.problems[problem] || 0) + 1;
      }
    }
  }
  const failureCounts = Object.values(failures)
    .map(entry => ({...entry, percent: (entry.count / count) * 100}))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const distribution = Array.from({length: total + 1}, (_, met) => ({met, count: 0}));
  for (const met of metValues) distribution[met].count += 1;

  return {
    count,
    total,
    min: sorted[0],
    max: sorted[count - 1],
    mean: metValues.reduce((sum, met) => sum + met, 0) / count,
    median,
    closeness: bounds(judgedCombos.map(judged => judged.closeness)),
    failureCounts,
    distribution,
  };
}

/** the mean, lowest and highest of a list of numbers (nulls left out), or null when empty */
export function bounds(values) {
  const list = values.filter(value => value !== null && value !== undefined);
  if (!list.length) return null;
  return {mean: mean(list), min: Math.min(...list), max: Math.max(...list)};
}

/** a short line for a failed target: "Sodium 2600mg over upper limit 2300mg" */
export function describeFailure(failure, decimals = 0) {
  const show = value => (value === null || value === undefined ? '?' : `${Number(value).toFixed(decimals)}${failure.unit}`);
  const parts = [];
  if (failure.problems.includes('no data')) {
    parts.push('no data');
    if (failure.target) parts.push(`target ${show(failure.target)}`);
    return `${failure.name}: ${parts.join(', ')}`;
  }
  if (failure.problems.includes('under')) parts.push(`${show(failure.amount)} of ${show(failure.target)} (${Math.round(failure.percent || 0)}%)`);
  if (failure.problems.includes('below lower limit')) parts.push(`${show(failure.amount)} below lower limit ${show(failure.lower)}`);
  if (failure.problems.includes('over upper limit')) parts.push(`${show(failure.amount)} over upper limit ${show(failure.upper)}`);
  return `${failure.name}: ${parts.join(', ')}`;
}

export const TOD_NUMBERS = {breakfast: BREAKFAST, lunch: LUNCH, dinner: DINNER, extras: 4};

/**
 * the meals of the published menus (GET v2/menu/:date, one a week: {stream: {menu: [{date, meals}]}})
 * dated within the range, each with the diet of its stream, a numeric time of day and the
 * nutrients by size the menu carries. Meals of a stream no diet is assigned to are left out and
 * the streams reported; the chef's choice entry (dated 'any') is not a day's meal.
 */
export function menuMeals(menus, diets, from, to) {
  const dietByStream = {};
  for (const diet of Object.values(diets || {})) {
    if (diet && diet.stream) dietByStream[diet.stream] = diet.id;
  }
  const meals = [];
  const streamsWithoutDiet = new Set();
  const seen = new Set();
  for (const menu of menus || []) {
    for (const [stream, streamMenu] of Object.entries(menu || {})) {
      if (!streamMenu || !Array.isArray(streamMenu.menu)) continue;
      const dietId = dietByStream[stream];
      for (const day of streamMenu.menu) {
        if (!day || day.date === 'any' || !Array.isArray(day.meals)) continue;
        if ((from && day.date < from) || (to && day.date > to)) continue;
        for (const meal of day.meals) {
          if (!meal || meal.id === undefined || meal.id === null) continue;
          if (!dietId) {
            streamsWithoutDiet.add(stream);
            continue;
          }
          const id = Number(String(meal.id).replace(/CC$/, ''));
          const key = `${dietId}:${id}:${day.date}`;
          if (Number.isNaN(id) || seen.has(key)) continue;
          seen.add(key);
          meals.push({
            id,
            name: meal.name || `meal ${id}`,
            diet: dietId,
            stream,
            tod: TOD_NUMBERS[meal.tod] || null,
            date: day.date,
            nutrients: meal.nutrients || null,
            // {tagId: 'has-beef'}: what a restriction looks for
            food_tags: meal.food_tags || null,
          });
        }
      }
    }
  }
  return {meals, streamsWithoutDiet: [...streamsWithoutDiet].sort()};
}

/**
 * the browser's judgement of a planned day against the server's (GET meals/nutrients, the same
 * meals and targets, judged from the database rather than the published menu): the targets the
 * two disagree on, and those whose amounts differ by more than half a percent. The server leaves
 * out nutrients with no amount, so those are not compared.
 */
export function compareWithServer(judged, serverRows) {
  const byId = {};
  for (const row of serverRows || []) {
    if (row && row.target && row.target.nutrient_id) byId[row.target.nutrient_id] = row;
  }
  const differences = [];
  let compared = 0;
  for (const result of judged.results || []) {
    const row = byId[result.nutrient_id];
    if (!row) continue;
    compared += 1;
    const serverMet = !row.problem;
    const serverAmount = Number(row.amount) || 0;
    const clientAmount = result.amount === null ? 0 : result.amount;
    const amountDiffers = Math.abs(serverAmount - clientAmount) > Math.max(0.005 * Math.abs(serverAmount), 1e-6);
    if (serverMet !== result.met || amountDiffers) {
      differences.push({
        nutrient_id: result.nutrient_id,
        name: result.name,
        unit: result.unit,
        clientMet: result.met,
        serverMet,
        clientAmount,
        serverAmount,
        verdictDiffers: serverMet !== result.met,
      });
    }
  }
  return {compared, notCompared: (judged.results || []).length - compared, differences};
}

// \bsaturated: the saturated fat total, not the poly/monounsaturated fats (EPA, DHA) a day should reach
export const LIMIT_NUTRIENT_PATTERN = /cholesterol|\bsaturated|trans|sodium|sucrose|sugar/i;

/** targets whose nutrient reads like a maximum but which are stored as a target value, i.e. a minimum to reach */
export function targetsLikelyLimits(targets) {
  return (targets || []).filter(target => target.target && !target.upper && LIMIT_NUTRIENT_PATTERN.test(target.name));
}

/** the targets whose nutrient no loaded meal carries any data for */
export function untrackedTargets(targets, nutrientsByMealId) {
  const symbols = new Set();
  for (const nutrients of Object.values(nutrientsByMealId || {})) {
    for (const symbol of Object.keys(nutrients || {})) symbols.add(symbol);
  }
  return (targets || []).filter(target => !target.symbol || !symbols.has(target.symbol));
}

/**
 * where to cut the y axis of a distribution chart so one dominant bar does not flatten the rest:
 * three times the median of the non-empty bars, or null when no bar would be cut
 */
export function clipCeiling(distribution) {
  const counts = distribution.map(bin => bin.count).filter(count => count > 0).sort((a, b) => a - b);
  if (counts.length < 3) return null;
  const median = counts[Math.floor(counts.length / 2)];
  const ceiling = Math.ceil(median * 3);
  const max = counts[counts.length - 1];
  return max > ceiling ? ceiling : null;
}

// --- diet presets and nutrition goals (meal filter definitions) ---

const GOAL_DIRECTION = /-(max|min)$/;

const limitNumber = value => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** 'preset' (a diet preset), 'goal' (a customer's <nutrient>-max / -min goal) or 'filter' */
export function filterKind(filter) {
  if (filter.is_preset) return 'preset';
  if (GOAL_DIRECTION.test(filter.id || '')) return 'goal';
  return 'filter';
}

function sentenceCase(text) {
  const lower = `${text || ''}`.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** "More protein" / "Less sodium" for a goal, the title (or id) for anything else; drafts marked */
export function filterLabel(filter) {
  const title = filter.title || filter.id;
  const direction = ((filter.id || '').match(GOAL_DIRECTION) || [])[1];
  const label = direction ? `${direction === 'max' ? 'More' : 'Less'} ${sentenceCase(title)}` : title;
  return filter.is_published === false ? `${label} (draft)` : label;
}

/**
 * a filter's day limits as this module's targets: a lower and/or upper limit per nutrient (the
 * generator ignores a filter's target value, so this does too). Limits are a day's, over three meals.
 */
export function filterTargets(filter, nutrientsById = {}) {
  return (filter.nutrient_targets || [])
    .map(row => {
      const nutrient = nutrientsById[row.nutrient_id] || {};
      return {
        nutrient_id: row.nutrient_id,
        symbol: nutrient.nutrientsymbol || row.nutrient_symbol || null,
        name: nutrient.nutrientname || row.nutrient_symbol || `nutrient ${row.nutrient_id}`,
        unit: nutrient.unit || '',
        target: null,
        lower: limitNumber(row.lower_limit),
        upper: limitNumber(row.upper_limit),
      };
    })
    .filter(target => target.lower || target.upper)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * the filters worth offering, as v-autocomplete items under group headers: diet presets, then the
 * nutrition goals customers pick, then the other filters. A filter with no limit and no restriction
 * is left out.
 */
export function filterOptions(filters) {
  const usable = (filters || []).filter(filter => filterTargets(filter).length || (filter.restrictions || []).length);
  const groups = [['preset', 'Diet presets'], ['goal', 'Nutrition goals'], ['filter', 'Other filters']];
  const items = [];
  for (const [kind, header] of groups) {
    const ofKind = usable
      .filter(filter => filterKind(filter) === kind)
      .map(filter => ({value: filter.id, text: filterLabel(filter), kind}))
      .sort((a, b) => a.text.localeCompare(b.text));
    if (ofKind.length) items.push({header}, ...ofKind);
  }
  return items;
}

/**
 * which of a filter's restrictions the meals break: for each, the meals carrying one of its
 * restricted tags and the tags found. A restriction with no definition or no tags is reported as unknown.
 */
export function restrictedMeals(meals, restrictionIds, restrictions = {}) {
  const broken = [];
  const unknown = [];
  for (const id of restrictionIds || []) {
    const restriction = restrictions[id];
    const tagIds = restriction && Array.isArray(restriction.restrictedTags) ? restriction.restrictedTags.map(String) : [];
    if (!tagIds.length) {
      unknown.push(id);
      continue;
    }
    const hits = meals
      .map(meal => ({meal, tags: tagIds.filter(tagId => meal.food_tags && meal.food_tags[tagId]).map(tagId => meal.food_tags[tagId])}))
      .filter(hit => hit.tags.length);
    if (hits.length) broken.push({id, text: restriction.text || id, meals: hits});
  }
  return {broken, unknown, checked: (restrictionIds || []).length - unknown.length};
}

/**
 * a day's meals against a diet preset or nutrition goal: its day limits on the summed nutrients
 * (scaled for a short day) and its restrictions on each meal. met needs every limit met and no
 * restricted meal. closeness averages each limit's closeness and, per restriction, the share of
 * meals clear of it; a day whose nutrients are missing is not judged.
 */
export function judgeFilter(meals, nutrientsByMealId, filter, nutrientsById = {}, restrictions = {}) {
  const targets = filterTargets(filter, nutrientsById);
  const limits = targets.length
    ? judgeMeals(meals, nutrientsByMealId, targets)
    : {meals, judged: meals.length > 0, complete: meals.length === MEALS_A_DAY, results: [], failures: [], met: 0, total: 0, score: null, closeness: null};
  const restricted = restrictedMeals(meals, filter.restrictions, restrictions);
  const clearShares = (filter.restrictions || [])
    .filter(id => !restricted.unknown.includes(id))
    .map(id => {
      const hit = restricted.broken.find(entry => entry.id === id);
      return meals.length ? ((meals.length - (hit ? hit.meals.length : 0)) / meals.length) * 100 : null;
    });
  const judged = limits.judged;
  return {
    filter,
    label: filterLabel(filter),
    kind: filterKind(filter),
    judged,
    limits,
    restricted,
    met: judged && limits.met === limits.total && restricted.broken.length === 0,
    closeness: judged ? mean([...limits.results.map(result => result.closeness), ...clearShares]) : null,
    failures: judged ? [...limits.failures.map(failure => failure.name), ...restricted.broken.map(entry => entry.text)] : [],
  };
}

/** over many judged days (swap combinations): how many meet the filter, the closeness bounds and what fails most */
export function filterSwapSummary(judgements) {
  const judged = judgements.filter(judgement => judgement.judged);
  const count = judged.length;
  if (!count) return {count: 0, met: 0, metPercent: null, closeness: null, failureCounts: []};
  const counts = {};
  for (const judgement of judged) {
    for (const failure of judgement.failures) counts[failure] = (counts[failure] || 0) + 1;
  }
  const met = judged.filter(judgement => judgement.met).length;
  return {
    count,
    met,
    metPercent: (met / count) * 100,
    closeness: bounds(judged.map(judgement => judgement.closeness)),
    failureCounts: Object.entries(counts)
      .map(([name, failures]) => ({name, count: failures, percent: (failures / count) * 100}))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
  };
}
